import { useState, useEffect } from 'react';
import type { OrderPricing } from '@/services/orders';
import { Card } from './Card';

interface CostProfitCardProps {
  readonly pricing: OrderPricing | null;
  readonly onSave?: (data: Record<string, unknown>) => void;
  readonly isPending?: boolean;
}

interface CostForm {
  materialCostEstimate: string;
  shippingLabelCost: string;
  packagingCost: string;
}

const initForm = (pricing: OrderPricing | null): CostForm => ({
  materialCostEstimate: pricing?.materialCostEstimate !== null ? String(pricing?.materialCostEstimate ?? '') : '',
  shippingLabelCost: pricing?.shippingLabelCost !== null ? String(pricing?.shippingLabelCost ?? '') : '',
  packagingCost: pricing?.packagingCost !== null ? String(pricing?.packagingCost ?? '') : '',
});

export const CostProfitCard = ({ pricing, onSave, isPending }: CostProfitCardProps) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<CostForm>(initForm(pricing));

  // Sync form when pricing changes externally (and not editing)
  useEffect(() => {
    if (!editing) {
      setForm(initForm(pricing));
    }
  }, [pricing, editing]);

  const updateField = (key: keyof CostForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (!onSave) return;

    const material = form.materialCostEstimate.trim() ? parseFloat(form.materialCostEstimate) : null;
    const label = form.shippingLabelCost.trim() ? parseFloat(form.shippingLabelCost) : null;
    const packaging = form.packagingCost.trim() ? parseFloat(form.packagingCost) : null;

    // Auto-calculate total internal cost and estimated profit
    const costs = [material, label, packaging].filter((v): v is number => v !== null);
    const totalInternalCost = costs.length > 0 ? costs.reduce((a, b) => a + b, 0) : null;
    const estimatedProfit = totalInternalCost !== null && pricing?.netPayout !== null && pricing?.netPayout !== undefined
      ? pricing.netPayout - totalInternalCost
      : null;

    onSave({
      materialCostEstimate: material,
      shippingLabelCost: label,
      packagingCost: packaging,
      totalInternalCost,
      estimatedProfit,
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setForm(initForm(pricing));
    setEditing(false);
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
          }}
        >
          Cost & profit
        </div>
        {!editing && onSave && pricing && (
          <span
            onClick={() => setEditing(true)}
            style={{ fontSize: '12px', color: 'var(--text-accent)', cursor: 'pointer' }}
          >
            Edit costs
          </span>
        )}
      </div>

      {!pricing ? (
        <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
          No cost data available
        </div>
      ) : editing ? (
        <div>
          <CostInput
            label="Material cost"
            value={form.materialCostEstimate}
            onChange={(v) => updateField('materialCostEstimate', v)}
            placeholder="0.00"
          />
          <CostInput
            label="Shipping label"
            value={form.shippingLabelCost}
            onChange={(v) => updateField('shippingLabelCost', v)}
            placeholder="0.00"
          />
          <CostInput
            label="Packaging cost"
            value={form.packagingCost}
            onChange={(v) => updateField('packagingCost', v)}
            placeholder="0.00"
          />

          <div className="flex justify-end" style={{ gap: '8px', marginTop: '10px' }}>
            <button
              onClick={handleCancel}
              style={{
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '36px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isPending}
              style={{
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '36px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--text-primary)',
                color: 'var(--bg-surface)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontWeight: 'var(--font-weight-medium)',
                opacity: isPending ? 0.6 : 1,
              }}
            >
              {isPending ? 'Saving...' : 'Save costs'}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Revenue group */}
          <Row
            label="Stripe fee"
            value={pricing.stripeFee !== null ? `-$${Math.abs(pricing.stripeFee).toFixed(2)}` : '--'}
          />
          <Row
            label="Net payout"
            value={pricing.netPayout !== null ? `$${pricing.netPayout.toFixed(2)}` : '--'}
          />

          <Divider />

          {/* Costs group */}
          <Row
            label="Material cost"
            value={pricing.materialCostEstimate !== null ? `-$${Math.abs(pricing.materialCostEstimate).toFixed(2)}` : '--'}
          />
          <Row
            label="Shipping label"
            value={pricing.shippingLabelCost !== null ? `-$${Math.abs(pricing.shippingLabelCost).toFixed(2)}` : '--'}
          />
          <Row
            label="Packaging cost"
            value={pricing.packagingCost !== null ? `-$${Math.abs(pricing.packagingCost).toFixed(2)}` : '--'}
          />
          <Row
            label="Total internal cost"
            value={pricing.totalInternalCost !== null ? `-$${Math.abs(pricing.totalInternalCost).toFixed(2)}` : '--'}
          />

          {/* Profit group */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '5px 0',
              fontSize: '13px',
              fontWeight: 'var(--font-weight-medium)',
              borderTop: '0.5px solid var(--border-default)',
              marginTop: '4px',
              paddingTop: '8px',
            }}
          >
            <span>Estimated profit</span>
            <span
              style={{
                color: pricing.estimatedProfit !== null
                  ? pricing.estimatedProfit >= 0
                    ? 'var(--color-success-600)'
                    : 'var(--color-error-600)'
                  : 'var(--text-primary)',
              }}
            >
              {pricing.estimatedProfit !== null ? `$${pricing.estimatedProfit.toFixed(2)}` : '--'}
            </span>
          </div>

          <Row
            label="Actual profit"
            value={pricing.actualProfit !== null ? `$${pricing.actualProfit.toFixed(2)}` : '--'}
            valueColor={
              pricing.actualProfit !== null
                ? pricing.actualProfit >= 0
                  ? 'var(--color-success-600)'
                  : 'var(--color-error-600)'
                : undefined
            }
          />

          <Row
            label="Refund amount"
            value={pricing.refundAmount !== null ? `$${pricing.refundAmount.toFixed(2)}` : '$0.00'}
          />
        </div>
      )}
    </Card>
  );
};

const Row = ({
  label,
  value,
  valueColor,
}: {
  readonly label: string;
  readonly value: string;
  readonly valueColor?: string;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '5px 0',
      fontSize: '13px',
    }}
  >
    <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
    <span style={{ color: valueColor ?? 'var(--text-primary)' }}>{value}</span>
  </div>
);

const CostInput = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '4px 0',
      fontSize: '13px',
    }}
  >
    <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>$</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="admin-input"
        style={{
          width: '80px',
          fontSize: '12px',
          textAlign: 'right',
          padding: '4px 6px',
        }}
      />
    </div>
  </div>
);

const Divider = () => (
  <div style={{ height: '0.5px', backgroundColor: 'var(--border-default)', margin: '8px 0' }} />
);
